import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import Navbar from '@/components/Navbar';
import StoryCard from '@/components/StoryCard';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Search as SearchIcon, X, Loader2 } from 'lucide-react';

export default function Search() {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  useEffect(() => {
    if (!query.trim()) {
      setResults([]);
      setSearched(false);
      return;
    }

    const search = async () => {
      setLoading(true);
      try {
        const res = await api.get(`/stories?search=${encodeURIComponent(query)}&limit=30`);
        setResults(res.data);
        setSearched(true);
      } catch (error) {
        console.error('Search error:', error);
      } finally {
        setLoading(false);
      }
    };

    const debounce = setTimeout(search, 400);
    return () => clearTimeout(debounce);
  }, [query]);

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      <main className="pt-24 pb-12 px-6 md:px-12">
        <div className="max-w-4xl mx-auto">
          {/* Search Input */}
          <div className="relative mb-8">
            <SearchIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-6 h-6 text-muted-foreground" />
            <Input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Buscar contos, autores, tags..."
              data-testid="search-input"
              autoFocus
              className="pl-14 pr-14 bg-surface border-border h-14 text-lg"
            />
            {query && (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setQuery('')}
                className="absolute right-4 top-1/2 -translate-y-1/2"
              >
                <X className="w-5 h-5" />
              </Button>
            )}
          </div>

          {/* Loading */}
          {loading && (
            <div className="flex justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-ruby" />
            </div>
          )}

          {/* Results */}
          {!loading && searched && (
            <>
              <p className="text-muted-foreground mb-6">
                {results.length} resultado{results.length !== 1 ? 's' : ''} encontrado{results.length !== 1 ? 's' : ''}
              </p>
              
              {results.length > 0 ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                  {results.map(story => (
                    <StoryCard key={story.id} story={story} />
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <p className="text-muted-foreground mb-4">
                    Nenhum resultado encontrado para "{query}"
                  </p>
                  <Button
                    variant="outline"
                    onClick={() => navigate('/browse')}
                    className="border-border"
                  >
                    Explorar todos os contos
                  </Button>
                </div>
              )}
            </>
          )}

          {/* Empty State */}
          {!loading && !searched && (
            <div className="text-center py-12">
              <SearchIcon className="w-16 h-16 text-muted mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-foreground mb-2">
                Encontra seu próximo conto
              </h2>
              <p className="text-muted-foreground">
                Busque por título, autor, categoria ou tags
              </p>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
